import React from "react";
import { message as antdMessage, notification as antdNotification } from "antd";

const MOBILE_BREAKPOINT = 767;
const DEFAULT_DURATION = 3;
const ERROR_DURATION = 60;
const GENERIC_TITLES = ["success", "error", "warning", "info"];
const CRUD_SUCCESS_KEY = "crud-operation-success";

let activeCrudSuccessKey;
let clearCrudSuccessKeyTimer;

const isMobileView = () =>
  typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;

const isRenderable = (value) =>
  value !== undefined && value !== null && value !== "";

const stringifyContent = (value) => {
  if (!isRenderable(value)) return "";
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) {
    return value.filter(isRenderable).join("\n");
  }
  if (typeof value === "object") {
    return value.message || value.description || value.content || "";
  }
  return value;
};

const buildContent = (options) => {
  if (!options || React.isValidElement(options) || typeof options !== "object") {
    return stringifyContent(options);
  }

  const title = stringifyContent(options.message || options.content);
  const description = stringifyContent(options.description);
  const normalizedTitle = typeof title === "string" ? title.trim().toLowerCase() : "";

  if (isRenderable(description) && GENERIC_TITLES.includes(normalizedTitle)) {
    return description;
  }

  if (isRenderable(description)) {
    return isRenderable(title) ? `${title}: ${description}` : description;
  }

  return title;
};

const normalizeOptions = (type, options, duration, onClose) => {
  const config = options && typeof options === "object" && !React.isValidElement(options)
    ? { ...options }
    : {};
  const title = stringifyContent(config.message || config.content) || buildContent(options);
  const description = stringifyContent(config.description);
  const normalizedTitle = typeof title === "string" ? title.trim().toLowerCase() : "";
  const notificationMessage =
    isRenderable(description) && GENERIC_TITLES.includes(normalizedTitle)
      ? description
      : title;
  const notificationDescription =
    isRenderable(description) && !GENERIC_TITLES.includes(normalizedTitle)
      ? description
      : undefined;

  delete config.message;
  delete config.description;
  delete config.content;
  delete config.type;

  return {
    duration: type === "error"
      ? ERROR_DURATION
      : duration ?? config.duration ?? DEFAULT_DURATION,
    onClose: onClose ?? config.onClose,
    ...config,
    message: notificationMessage,
    description: notificationDescription,
    className: [
      "global-notification",
      `global-notification-${type}`,
      config.className,
    ].filter(Boolean).join(" "),
    style: {
      maxWidth: isMobileView() ? "calc(100vw - 32px)" : 568,
      margin: "0 auto",
      ...config.style,
    },
  };
};

const useCrudSuccessKey = (type, config) => {
  if (type !== "success" || config.key || !activeCrudSuccessKey) return config;

  return {
    ...config,
    key: activeCrudSuccessKey,
  };
};

const trackCrudSuccess = (key) => {
  activeCrudSuccessKey = key;
  clearTimeout(clearCrudSuccessKeyTimer);
  clearCrudSuccessKeyTimer = setTimeout(() => {
    activeCrudSuccessKey = undefined;
    clearCrudSuccessKeyTimer = undefined;
  }, 0);
};

const open = (type, options, duration, onClose) => {
  const config = useCrudSuccessKey(
    type,
    normalizeOptions(type, options, duration, onClose)
  );
  if (!isRenderable(config.message)) return undefined;

  antdNotification.config({
    top: isMobileView() ? 64 : 24,
    duration: DEFAULT_DURATION,
    maxCount: 3,
  });

  const notificationMethod = antdNotification[type] || antdNotification.open;
  return notificationMethod(config);
};

const openCrudSuccess = (options, duration, onClose) => {
  const config = options && typeof options === "object" && !React.isValidElement(options)
    ? { ...options }
    : { message: options };
  const key = config.key || CRUD_SUCCESS_KEY;

  trackCrudSuccess(key);
  return open("success", { ...config, key }, duration, onClose);
};

const globalMessage = {
  open: (options) => open(options?.type || "info", options),
  success: (options, duration, onClose) => open("success", options, duration, onClose),
  crudSuccess: (options, duration, onClose) => openCrudSuccess(options, duration, onClose),
  error: (options, duration, onClose) => open("error", options, duration, onClose),
  warning: (options, duration, onClose) => open("warning", options, duration, onClose),
  warn: (options, duration, onClose) => open("warning", options, duration, onClose),
  info: (options, duration, onClose) => open("info", options, duration, onClose),
  loading: (options, duration, onClose) => open("loading", options, duration, onClose),
  destroy: (key) => antdNotification.destroy(key),
  config: (options) => antdNotification.config(options),
};

// Older pages still import antd's message helper directly. Route successful
// messages through the notification standard so they can reuse a CRUD key.
antdMessage.success = (options, duration, onClose) =>
  globalMessage.success(options, duration, onClose);

export default globalMessage;
